import axios from 'axios';
import { subDays, format, addDays } from 'date-fns'
import { ConditionDetail, Extreme, Line } from './models';
import { Point } from './models/stationLight';
import { Candidate } from './../../components/search/Candidate';
import { APIPath } from '../../ekispert/extreme';

import Ekispert from '../../utils/ekispert';
import { Lang } from '../../states/query';
import DateTime from '../../utils/dateTime';

/**
 * インクリメンタルサーチをする際に特定の文字列をエスケープする
 * @param input 入力文字列
 */
export function escapeString(input: string) {
  return input.replace(/[&=$?]/g, '');
}
interface ApiConfig {
  baseURL: string;
  timeout: number;
}

const DEFAULT_API_CONFIG: ApiConfig = {
  baseURL: `${process.env['REACT_APP_API_ENDPOINT']}/ekispert/` || '',
  timeout: 7000,
};

export const getEkispertAPIFactory = (optionConfig?: ApiConfig) => {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);

  const getEkispertAPI = async (conditions: object, path: APIPath) => {
    axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
    // リクエストパラメーター。
    const params = {
      ...conditions,
    };
    try {
      const response = await instance.get(path, { params });
      if (response.status !== 200) {
        throw new Error('Server Error');
      }
      // 各APIに応じてResponseを成形して返す
      switch(path){
        case APIPath.Extreme:
          // レスポンス内にCourse要素が存在しない場合。
          if (!response.data.ResultSet.Course) {
            throw new Error('Not Found Course');
          }
          const extreme: Extreme = response.data;
          // APIレスポンスの経路を返す。
          return extreme.ResultSet.Course;
        case APIPath.PassStation:
          return response.data.ResultSet.Point;
        case APIPath.Condition:
          const condition: ConditionDetail = response.data;
          return condition.ResultSet.Condition;
        case APIPath.GeoStation:
        case APIPath.AddressStation:
          return response.data.ResultSet.Point;
        case APIPath.TrainInfo:
          return response.data.ResultSet;
        case APIPath.LightStation:
          return response.data.ResultSet.Point.Station;
        default:
          return response.data.ResultSet;
      }
    } catch(err) {
      throw err;
    }
  }
  return getEkispertAPI;
};


const getRail = async (conditions: object) => {
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(DEFAULT_API_CONFIG);
  // リクエストパラメーター。
  const params = {
    ...conditions,
  };
  try {
    const response = await instance.get(`rail`, { params });
    return response;
  } catch(err) {
    throw err;
  }
}

// 発着時間を追加したLine
export interface AddTimeLine extends Line {
  depTime: Date,
  arrTime: Date
}

/**
 * 駅すぱあとの前後のダイヤ一覧から取得しら時刻文字列よりDate型に復元させる
 * @param baseDate 基準となる日付
 * @param dateString 駅すぱあとの前後のダイヤ一覧から取得した時刻文字列（ 例: 08:55:00+09:00 ）
 */
export const getRestoreTime = (baseDate: Date, dateString: string) => {
  const baseFormat: string = DateTime.JSTFormat(baseDate, 'yyyy/MM/dd');
  const getTimeRegExp = /(\d{2}:\d{2}:\d{2}\+\d{2}:\d{2})/;
  const match = dateString.match(getTimeRegExp);
  if(match && match[1]) {
    return new Date(`${baseFormat} ${match[1]}`);
  }
  return null;
}

export const filterTrainList = (lines: Line[], selectedLine: Line, lang: Lang) => {
  const range = 5;
  // 配列であればそのまま、配列ではない場合は配列化して格納する。
  lines = (lines instanceof Array) ? lines: [lines];
  let matchIdx: number = -1;
  const depTime = selectedLine.DepartureState.Datetime.text.split('T')[1];
  const arrTime = selectedLine.ArrivalState.Datetime.text.split('T')[1];
  // 基準となる日付を選択した路線情報から求める
  let baseDate = new Date(selectedLine.ArrivalState.Datetime.text);
  // 選択した路線が 00:00 を過ぎる列車だった場合、前日を基準日とする
  if(selectedLine.ArrivalState.Datetime.operation === 'yesterday' ){
    baseDate = subDays(baseDate, 1);
  }
  let setLines: AddTimeLine[] = lines.map((line:Line, idx:number) => {
    const setLine: AddTimeLine = {
      ...line,
      depTime: new Date(),
      arrTime: new Date()
    };
    const baseDepDate = (line.DepartureState.Datetime.operation ===  'yesterday')? addDays(baseDate, 1): baseDate;
    const getDepDate: Date | null = getRestoreTime(baseDepDate, line.DepartureState.Datetime.text);
    if(getDepDate !== null) {
      setLine.depTime = getDepDate;
    }
    const baseArrDate = (line.ArrivalState.Datetime.operation ===  'yesterday')? addDays(baseDate, 1): baseDate;
    const getArrDate: Date | null = getRestoreTime(baseArrDate, line.ArrivalState.Datetime.text);
    if(getArrDate !== null) {
      setLine.arrTime = getArrDate;
    }
    // 出発時刻・到着時刻が一致するか判定
    if(matchIdx < 0 && line.DepartureState.Datetime.text === depTime && line.ArrivalState.Datetime.text === arrTime ){
      matchIdx = idx;
    }
    return setLine;
  });

  // 路線名を号車番号（Number）要素を考慮して設定する。
  setLines = setLines.map((line: AddTimeLine) => {
    const lineType = (typeof line.Type === 'string') ? line.Type : line.Type.text;
    // 日本語版と英語版の鉄道の場合は号車番号があれば付与する。
    if (lang === Lang.ja || lineType === 'train') {
      line.Name = Ekispert.setLineName(line.Name, line.Type, line.Number, lang);
    }
    return line;
  })

  // 前後の列車に分割
  if(matchIdx >= 0){
    return {
      before: setLines.slice((matchIdx - range < 0? 0: matchIdx - range), matchIdx),
      current: setLines[matchIdx],
      after: setLines.slice(matchIdx + 1, matchIdx + range + 1)
    }
  }
  return null;
}

/**
 * 前後5本と該当の列車に分離します
 * @param conditions /rail に渡すためのパラメーター
 * @param selectedLine 選択している列車のObject
 * @param lang アプリケーション上で指定されている言語設定の値。
 */
export const getZengoTrain = async (conditions: object, selectedLine: Line, lang: Lang) => {
  const response = await getRail(conditions);
  const lines: Line[] = response.data.ResultSet.Line;
  if(typeof lines === 'undefined'){
    return null;
  }
  return filterTrainList(lines, selectedLine, lang);
}

export const getCourseEdit = async (conditions: object) => {
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(DEFAULT_API_CONFIG);
  // リクエストパラメーター。
  const params = {
    ...conditions,
  };
  try {
    const response = await instance.get(`closed/course/edit`, { params });
    return response;
  } catch(err) {
    throw err;
  }
}

/**
 * データバージョンを取得する
 */
export async function getDataVersion() {
  const config = {
    ...DEFAULT_API_CONFIG,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);
  const params = {};
  try {
    const response = await instance.get('dataversion', { params });
    if (response.status !== 200) {
      throw new Error('Server Error');
    }
    return response.data.ResultSet;
  } catch (err) {
    throw err;
  }
}

/**
 * 駅簡易情報（/station/light）を取得する。
 * （主に駅名のインクリメンタルサーチ用途。）
 * @param input テキストボックスで入力された値。
 * @param lang 言語指定（ja/en)
 * @param optionConfig 独自で指定したAPIの設定など（任意）
 */
export async function getClosedStationLight (input: string, lang:string = 'ja', optionConfig?: object) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);

  const params = {
    'name': escapeString(input),
    'language': lang,
    'type': 'train:ship:bus',
    'stationNameType': 'unique',
  };

  // あとで見直す（2019/12/23現在）
  // eslint-disable-next-line no-useless-catch
  try {
    if(params.name === '') {
      throw new Error('入力値{name}が空です');
    }
    const response = await instance.get(`station/light`, { params });

    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    let points: Point[] = [];

    // 取得したレスポンスがundefinedではない場合に、レスポンスの値を代入する。
    if (typeof response.data.ResultSet.Point !== undefined && response.data.ResultSet.Point !== undefined) {
      // 取得した簡易駅情報が配列の場合はそのまま、オブジェクトの場合は配列にはめる。
      points = (response.data.ResultSet.Point instanceof Array) ? response.data.ResultSet.Point : [response.data.ResultSet.Point];
    }

    const stations = points.length === 0 ? [] : points.map(p => (new Candidate(p.Station.Name, p.Station.code, p.Station.Type)));
    return stations;
  } catch (err) {
    // dev環境のみエラー内容を出力する
    if(process.env['REACT_APP_STAGE_ENV'] === 'dev') {
      console.error("インクリメンタルサーチ(/station/light)にてエラーが発生しました。");
      console.error(err);
    }
    return [];
  }
}

/**
 * 駅詳細情報（/station/）を取得する
 * （広告の出発・到着駅の緯度経度を取得するため）
 * @param code 取得する駅コード
 * @param optionConfig 独自で指定したAPIの設定など（任意）
 */
export async function getClosedStationDetail(code: string, optionConfig?: object) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);

  const params = {
    'code': code,
    'type': 'train:ship:bus',
    'gcs': 'wgs84'
  };
  // あとで見直す（2019/12/23現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`station`, { params });

    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    // 取得したレスポンスがundefinedではない場合に、レスポンスの値を代入する。
    if (typeof response.data.ResultSet.Point !== undefined && response.data.ResultSet.Point !== undefined) {
      return response.data.ResultSet.Point;
    }
  } catch (err) {
    throw err;
  }
}

/**
 * 空路平均路線を取得する（/rail）
 * @param depStationCode 出発地駅コード
 * @param arrStationCode 目的地駅コード
 * @param optionConfig 独自で指定したAPIの設定など（任意）
 */
export async function getAverageRail(depStationCode: string, arrStationCode: string, optionConfig?: object) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);
  const params = {
    'type': 'plane',
    'from': depStationCode,
    'to': arrStationCode
  };

  try {
    const response = await instance.get(`rail`, { params });
    if (response.status !== 200) {
      throw new Error('Server Error');
    }
    if(response.data.ResultSet) {
      return response.data.ResultSet;
    }
    else {
      throw new Error('/rail Response is Wrong!')
    }
  }
  catch(e) {
    throw e;
  }
}

/**
 * 空路時刻表を取得する  (/plane/timetable)
 * @param railName 対応する路線名（/railで取得した名前）
 * @param date 取得する日付
 * @param direction 上りかどうか
 * @param optionConfig 独自で指定したAPIの設定など（任意）
 */
export async function getPlaneTimeTbile(railName: string, date: Date, direction: boolean, optionConfig?: object) {
  const config = {
    ...DEFAULT_API_CONFIG,
    ...optionConfig,
  };
  axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
  const instance = axios.create(config);
  const yyyymmdd = DateTime.JSTFormat(date, 'yyyyMMdd');
  const params = {
    'railName': railName,
    'direction': direction.toString(),
    'date': yyyymmdd
  };

  try {
    const response = await instance.get(`plane/timetable`, { params });
    if (response.status !== 200) {
      throw new Error('Server Error');
    }
    if(response.data.ResultSet) {
      return response.data.ResultSet;
    }
    else {
      throw new Error('/plane/timetable Response is Wrong!')
    }
  }
  catch(e) {
    throw e;
  }
}
